(() => {
  if (customElements.get('zd-mvp-card-options')) return;

  class ModelCardOptions extends HTMLElement {
    connectedCallback() {
      this.onChange = event => {
        const input = event.target;
        if (!input.matches('input[type="radio"]') || !input.checked) return;
        const card = this.closest('.card-wrapper');
        const image = card?.querySelector('.card__media img');
        if (image) {
          image.removeAttribute('srcset');
          image.src = input.dataset.image;
          image.alt = input.value;
        }
        this.querySelector('[role="status"]').textContent = input.value;
        for (const link of card.querySelectorAll('a.full-unstyled-link, a.zd-mvp-native-more')) {
          const url = new URL(link.href, location.origin);
          url.searchParams.set('variant', input.dataset.variantId);
          link.href = url.href;
        }
        if (this.hasAttribute('data-compact')) this.fitChoices();
      };
      this.addEventListener('change', this.onChange);
      if (this.hasAttribute('data-compact')) {
        this.optionsRow = this.querySelector('.zd-mvp-native-options');
        this.choices = Array.from(this.querySelectorAll('.zd-mvp-native-choice'));
        this.moreLink = this.querySelector('.zd-mvp-native-more');
        this.resizeObserver = new ResizeObserver(() => {
          cancelAnimationFrame(this.fitFrame);
          this.fitFrame = requestAnimationFrame(() => this.fitChoices());
        });
        this.resizeObserver.observe(this.optionsRow);
        this.fitChoices();
      }
    }

    fitChoices() {
      const width = this.optionsRow.clientWidth;
      if (!width || !this.choices.length) return;
      const choiceWidth = parseFloat(getComputedStyle(this.choices[0]).width) || 44;
      const gap = parseFloat(getComputedStyle(this.optionsRow).columnGap) || 0;
      const slots = Math.max(1, Math.floor((width + gap) / (choiceWidth + gap)));
      const visibleCount = this.choices.length <= slots ? this.choices.length : slots - 1;
      const visibleChoices = new Set(this.choices.slice(0, visibleCount));
      const selected = this.choices.find(choice => choice.querySelector('input:checked'));
      if (visibleCount && selected && !visibleChoices.has(selected)) {
        visibleChoices.delete(this.choices[visibleCount - 1]);
        visibleChoices.add(selected);
      }
      for (const choice of this.choices) {
        choice.hidden = !visibleChoices.has(choice);
        if (!choice.hidden) {
          const template = choice.querySelector('[data-zd-model-thumbnail]');
          if (template) template.replaceWith(template.content.cloneNode(true));
        }
      }
      const hiddenCount = this.choices.length - visibleCount;
      this.moreLink.hidden = hiddenCount === 0;
      this.moreLink.textContent = `+${hiddenCount}`;
    }

    disconnectedCallback() {
      this.removeEventListener('change', this.onChange);
      this.resizeObserver?.disconnect();
      cancelAnimationFrame(this.fitFrame);
    }
  }
  customElements.define('zd-mvp-card-options', ModelCardOptions);
})();

// Align the information within each rendered row, including after filtering.
(() => {
  class CatalogRows {
    constructor(root) {
      this.root = root;
      this.schedule = () => {
        cancelAnimationFrame(this.frame);
        this.frame = requestAnimationFrame(() => this.layout());
      };
      this.mutations = new MutationObserver(this.schedule);
      this.mutations.observe(root, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['data-number-of-reviews'],
      });
      this.resize = new ResizeObserver(entries => {
        if (entries.some(entry => entry.contentRect.width !== this.width)) this.schedule();
      });
      window.addEventListener('resize', this.schedule);
      document.fonts.ready.then(this.schedule);
      this.schedule();
    }

    layout() {
      if (!this.root.isConnected) return;
      const grid = this.root.querySelector('.product-grid');
      if (!grid) return;
      if (grid !== this.grid) {
        this.resize.disconnect();
        this.grid = grid;
        this.resize.observe(grid);
      }
      this.width = grid.getBoundingClientRect().width;
      if (!this.width) return;
      const items = Array.from(grid.children).filter(item => item.matches('.grid__item'));

      // Read natural heights first; a wider viewport can need less space again.
      for (const item of items) {
        item.style.removeProperty('--zd-row-title-height');
        item.style.removeProperty('--zd-row-price-height');
        item.removeAttribute('data-zd-row-models');
      }
      const rows = [];
      for (const item of items) {
        const card = item.querySelector('.zd-mvp-catalog-card');
        if (!card) continue;
        const heading = Array.from(card.querySelectorAll('.card__heading'))
          .find(element => element.getClientRects().length);
        const price = card.querySelector('.card-information > .price');
        if (!heading || !price) continue;
        const top = item.getBoundingClientRect().top;
        let row = rows[rows.length - 1];
        if (!row || Math.abs(row.top - top) > 1) {
          row = { top, items: [], titleHeight: 0, priceHeight: 0, hasModels: false };
          rows.push(row);
        }
        row.items.push(item);
        row.titleHeight = Math.max(row.titleHeight, heading.getBoundingClientRect().height);
        row.priceHeight = Math.max(row.priceHeight, price.getBoundingClientRect().height);
        row.hasModels ||= Boolean(card.querySelector('zd-mvp-card-options'));
      }
      for (const row of rows) {
        for (const item of row.items) {
          item.style.setProperty('--zd-row-title-height', `${Math.ceil(row.titleHeight)}px`);
          item.style.setProperty('--zd-row-price-height', `${Math.ceil(row.priceHeight)}px`);
          item.toggleAttribute('data-zd-row-models', row.hasModels);
        }
      }
    }

    destroy() {
      cancelAnimationFrame(this.frame);
      this.mutations.disconnect();
      this.resize.disconnect();
      window.removeEventListener('resize', this.schedule);
    }
  }

  let layout;
  const mount = () => {
    const root = document.getElementById('ProductGridContainer');
    if (layout?.root === root) return;
    layout?.destroy();
    layout = root ? new CatalogRows(root) : null;
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', mount, { once: true });
  else mount();
  document.addEventListener('shopify:section:load', mount);
})();
